import { useLinkTo, useNavigation } from '@react-navigation/native';
import { useEffect, useMemo } from 'react';
import { StyleSheet, Text, TextStyle, View, useWindowDimensions } from 'react-native';
import Svg, { Defs, RadialGradient, Rect, Stop } from 'react-native-svg';
import theme from 'theme';
import { formatDayAndMonth, formatShortTime, isToday } from 'utils/date';
import { isMobileOrSmallScreen } from 'utils/deviceInfo';
import { calcSizeForDevice } from 'utils/index';
import { Platform } from 'utils/platform';

import NewGradientButton from 'components/Button/NewGradienteButton';
import ArrowRight from 'components/Icons/ArrowRight';
import { usePlayer } from 'components/Player/context';

export interface TransmitionCatchProps {
  endTime?: string;
  startTime?: string;
}

const CATALOG_PATH = '/assista';
const FALLBACK_START_TIME_LABEL = 'Disponível em breve';
const DARK_BLUE = theme.colors.base.darkBlue;
const BACKGROUND_GLOW = '#080D49';
const TITLE_GRADIENT_TOP = '#6DFFBC';
const TITLE_GRADIENT_BOTTOM = '#00AD5C';
const TITLE_SOLID_COLOR = '#37D68C';
const FONT_ASCENT = 0.968;
const FONT_DESCENT = 0.242;
const FONT_CAP_HEIGHT = 0.727;

const getTitleStyle = (fontSize: number, lineHeight: number): TextStyle => {
  if (Platform.OS !== 'web') {
    return { color: TITLE_SOLID_COLOR };
  }

  const halfLeading = (lineHeight - (FONT_ASCENT + FONT_DESCENT) * fontSize) / 2;
  const capTop = halfLeading + (FONT_ASCENT - FONT_CAP_HEIGHT) * fontSize;
  const baseline = halfLeading + FONT_ASCENT * fontSize;
  const start = ((capTop / lineHeight) * 100).toFixed(2);
  const end = ((baseline / lineHeight) * 100).toFixed(2);

  return {
    backgroundImage: `linear-gradient(180deg, ${TITLE_GRADIENT_TOP} ${start}%, ${TITLE_GRADIENT_BOTTOM} ${end}%)`,
    backgroundSize: `100% ${lineHeight}px`,
    backgroundRepeat: 'repeat-y',
    backgroundClip: 'text',
    color: 'transparent',
    WebkitTextFillColor: 'transparent',
  } as unknown as TextStyle;
};

const BackgroundGradient = () => (
  <Svg
    pointerEvents="none"
    width="100%"
    height="100%"
    style={StyleSheet.absoluteFill}
    preserveAspectRatio="none">
    <Defs>
      <RadialGradient id="leftGlow" cx="10%" cy="25%" r="40%">
        <Stop offset="0%" stopColor={BACKGROUND_GLOW} />
        <Stop offset="100%" stopColor={BACKGROUND_GLOW} stopOpacity="0" />
      </RadialGradient>
      <RadialGradient id="rightGlow" cx="100%" cy="100%" r="38%">
        <Stop offset="0%" stopColor={BACKGROUND_GLOW} />
        <Stop offset="100%" stopColor={BACKGROUND_GLOW} stopOpacity="0" />
      </RadialGradient>
    </Defs>
    <Rect width="100%" height="100%" fill="url(#leftGlow)" />
    <Rect width="100%" height="100%" fill="url(#rightGlow)" />
  </Svg>
);

const buildStartTimeLabel = (startTime: string) => {
  const startsAt = new Date(startTime);

  if (isNaN(startsAt.getTime())) {
    return FALLBACK_START_TIME_LABEL;
  }

  const day = isToday(startsAt) ? 'hoje' : `em ${formatDayAndMonth(startsAt)}`;

  return `Disponível ${day}, às ${formatShortTime(startsAt)}`;
};

const TransmitionCatch: React.FC<TransmitionCatchProps> = ({ endTime, startTime }) => {
  const linkTo = useLinkTo();
  const navigation = useNavigation();
  const { width } = useWindowDimensions();
  const { setHasTransmissionError, setLastWatchedMedia, setLastWatchedMediaTime } = usePlayer();
  const isCompact = isMobileOrSmallScreen(width);

  useEffect(() => {
    return navigation.addListener('beforeRemove', async () => {
      setHasTransmissionError(false);
      setLastWatchedMedia(null);
      setLastWatchedMediaTime(null);
    });
  }, []);

  useEffect(() => {
    setHasTransmissionError(true);
  }, []);

  const content = useMemo(() => {
    if (endTime) {
      return {
        title: 'A transmissão foi encerrada',
        description: 'Agradecemos por assistir! Que tal aproveitar outro conteúdo?',
      };
    }

    if (startTime) {
      return {
        title: buildStartTimeLabel(startTime),
        description: 'Que tal assistir a outro conteúdo enquanto espera?',
      };
    }

    return null;
  }, [endTime, startTime]);

  if (!content) return null;

  const arrowSize = String(calcSizeForDevice(isCompact ? 16 : 18, 24));
  const titleFontSize = calcSizeForDevice(isCompact ? 28 : 48, 80);
  const titleLineHeight = calcSizeForDevice(isCompact ? 36 : 58, 92);

  return (
    <View style={styles.container}>
      <BackgroundGradient />
      <View style={[styles.content, { maxWidth: calcSizeForDevice(900, 1400) }]}>
        <Text
          style={[
            styles.title,
            { fontSize: titleFontSize, lineHeight: titleLineHeight },
            getTitleStyle(titleFontSize, titleLineHeight),
          ]}>
          {content.title}
        </Text>

        <Text
          style={[
            styles.description,
            {
              fontSize: calcSizeForDevice(isCompact ? 16 : 24, 32),
              lineHeight: calcSizeForDevice(isCompact ? 22 : 32, 42),
              marginTop: calcSizeForDevice(isCompact ? 16 : 32, 44),
              marginBottom: calcSizeForDevice(isCompact ? 20 : 24, 32),
            },
          ]}>
          {content.description}
        </Text>

        <View style={{ width: calcSizeForDevice(isCompact ? 220 : 232, 312) }}>
          <NewGradientButton
            useTVFocus
            hasTVPreferredFocus
            borderRadius={calcSizeForDevice(24, 32)}
            style={{ height: calcSizeForDevice(48, 64) }}
            onPress={() => linkTo(CATALOG_PATH)}>
            <View style={[styles.buttonContent, { gap: calcSizeForDevice(8, 12) }]}>
              <Text
                style={[
                  styles.buttonText,
                  { fontSize: calcSizeForDevice(isCompact ? 14 : 16, 22) },
                ]}>
                Explorar catálogo
              </Text>
              <ArrowRight width={arrowSize} height={arrowSize} color={DARK_BLUE} />
            </View>
          </NewGradientButton>
        </View>
      </View>
    </View>
  );
};

export default TransmitionCatch;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    backgroundColor: DARK_BLUE,
  },
  content: {
    width: '100%',
    alignItems: 'center',
  },
  title: {
    fontWeight: 'bold',
    textAlign: 'center',
  },
  description: {
    fontWeight: '600',
    color: theme.colors.base.white,
    textAlign: 'center',
  },
  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonText: {
    fontWeight: 'bold',
    color: DARK_BLUE,
  },
});
